/**
 * Builds a user-facing message from a DRF 400 validation body.
 *
 * The BE rejects invalid payloads with field-keyed bodies (e.g.
 * `{"filter_rules": ["Value direction needs a Field filter …"]}`) or
 * `detail`/`non_field_errors` — never the `message` key the generic fallbacks
 * look for, so without this the banner shows axios's "Request failed with
 * status code 400". Only 400 bodies are surfaced; other statuses keep the
 * generic copy so unexpected server output is never shown verbatim.
 *
 * @param {unknown} error - The axios rejection
 * @param {string} fallback - Copy to use when no validation messages are found
 * @returns {string} The joined validation messages, or the axios/fallback message
 */
const extractValidationErrorMessage = (error: unknown, fallback: string): string => {
  const axiosError = error as { response?: { status?: number; data?: unknown }; message?: string };
  const data = axiosError.response?.status === 400 ? axiosError.response.data : undefined;
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const messages = Object.values(data).flatMap((value) => {
      if (typeof value === 'string') return [value];
      if (Array.isArray(value)) return value.filter((item): item is string => typeof item === 'string');
      return [];
    });
    if (messages.length > 0) {
      return messages.join(' ');
    }
  }
  return axiosError.message || fallback;
};

export { extractValidationErrorMessage };
